import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { AppShell } from '../../app/AppShell'
import { NoticeList } from '../../app/NoticeList'
import { ApiError } from '../../api/problem'
import { useCaseSheet, useDeleteCase } from '../../domains/cases/queries'
import { useCaseEditing } from '../../domains/cases/useCaseEditing'
import { CaseScenesCard } from '../../domains/cases/sections/CaseScenesCard'
import { MajorCharactersCard } from '../../domains/cases/sections/MajorCharactersCard'
import { Banner, Button, Card, Input, SectionLabel } from '../../design/components'
import styles from './play.module.css'

/**
 * One case: what it is about, who it turns on, and the scenes it runs through.
 *
 * Members read; the Game Master writes — the server's `isGameMaster` decides, and every
 * affordance on this page hangs off it rather than off anything guessed from the data.
 */
export function CaseDetailPage() {
  const params = useParams<{ caseId: string }>()
  const caseId = Number(params.caseId)

  const query = useCaseSheet(caseId)
  const core = query.data?.sections.core
  const editing = useCaseEditing(caseId, query.data?.isGameMaster === true)

  if (Number.isNaN(caseId)) {
    return <CaseMessage message="That case id is not a number." />
  }

  if (query.isPending) {
    return <CaseMessage message="Loading…" subtitle="FETCHING CASE" />
  }

  if (query.isError) {
    const missing =
      query.error instanceof ApiError &&
      (query.error.kind === 'not-found' || query.error.kind === 'forbidden')

    return (
      <CaseMessage
        message={missing ? 'No such case, or it is not one of yours.' : 'Could not load this case.'}
        tone={missing ? 'info' : 'error'}
      />
    )
  }

  const sheet = query.data

  return (
    <AppShell
      title={core?.name ?? 'Case'}
      subtitle={subtitleFor(core?.campaignName, core?.sceneCount, core?.majorCharacterCount)}
    >
      <NoticeList notices={editing.notices} failure={editing.failure} onDismiss={editing.dismiss} />

      {core?.description !== undefined && core.description !== '' && (
        <Card variant="aspects" label="The case">
          <p style={{ whiteSpace: 'pre-wrap' }}>{core.description}</p>
        </Card>
      )}

      <CaseScenesCard caseId={caseId} scenes={sheet.sections.scenes} editing={editing} />
      <MajorCharactersCard
        caseId={caseId}
        characters={sheet.sections.characters}
        editing={editing}
      />

      {sheet.isGameMaster && core !== undefined && (
        <GameMasterCard caseId={caseId} core={core} editing={editing} />
      )}

      <Link to={`/campaigns/${sheet.campaignId}/cases`} className={styles.backLink}>
        <Button variant="secondary" fullWidth>
          All cases
        </Button>
      </Link>
    </AppShell>
  )
}

function GameMasterCard({
  caseId,
  core,
  editing,
}: {
  caseId: number
  core: { name: string; description: string; campaignId: number }
  editing: ReturnType<typeof useCaseEditing>
}) {
  const navigate = useNavigate()
  const remove = useDeleteCase()

  const [editingCase, setEditingCase] = useState(false)
  const [name, setName] = useState(core.name)
  const [description, setDescription] = useState(core.description)
  const [confirming, setConfirming] = useState(false)

  return (
    <Card variant="stunts" label="Game Master">
      {editingCase ? (
        <form
          className={styles.form}
          onSubmit={(event) => {
            event.preventDefault()
            editing.update.mutate(
              { name, description: description === '' ? null : description },
              { onSuccess: () => setEditingCase(false) },
            )
          }}
        >
          <Input label="Name" value={name} autoFocus onChange={(e) => setName(e.target.value)} />
          <label className={styles.field}>
            <SectionLabel tone="muted" micro>
              Description
            </SectionLabel>
            <textarea
              className={styles.textarea}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>
          <div className={styles.formActions}>
            <Button
              variant="secondary"
              onClick={() => {
                setName(core.name)
                setDescription(core.description)
                setEditingCase(false)
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={editing.update.isPending || name.trim() === ''}>
              {editing.update.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      ) : (
        <div className={styles.formActions}>
          <Button variant="secondary" onClick={() => setEditingCase(true)}>
            Edit the case
          </Button>
        </div>
      )}

      <div className={styles.danger}>
        {confirming ? (
          <>
            <p className={styles.note}>
              Deleting the case removes every scene in it, with their aspects, cast, panels and
              pages. The characters themselves are untouched.
            </p>
            <div className={styles.formActions}>
              <Button variant="secondary" onClick={() => setConfirming(false)}>
                Keep it
              </Button>
              <Button
                variant="danger"
                disabled={remove.isPending}
                onClick={() =>
                  remove.mutate(caseId, {
                    onSuccess: () => void navigate(`/campaigns/${core.campaignId}/cases`),
                  })
                }
              >
                {remove.isPending ? 'Deleting…' : 'Delete case'}
              </Button>
            </div>
          </>
        ) : (
          <Button variant="danger" fullWidth onClick={() => setConfirming(true)}>
            Delete case
          </Button>
        )}
      </div>
    </Card>
  )
}

function CaseMessage({
  message,
  subtitle,
  tone = 'info',
}: {
  message: string
  subtitle?: string
  tone?: 'info' | 'error'
}) {
  return (
    <AppShell title="Case" {...(subtitle !== undefined && { subtitle })}>
      <Card>
        <Banner tone={tone}>{message}</Banner>
      </Card>
      <Link to="/campaigns" className={styles.backLink}>
        <Button variant="secondary" fullWidth>
          All campaigns
        </Button>
      </Link>
    </AppShell>
  )
}

/** e.g. "CHICAGO BY NIGHT · 3 SCENES · 2 MAJOR". */
function subtitleFor(
  campaignName: string | null | undefined,
  scenes: number | undefined,
  major: number | undefined,
): string | undefined {
  const parts: string[] = []
  if (campaignName !== null && campaignName !== undefined && campaignName !== '') {
    parts.push(campaignName)
  }
  if (scenes !== undefined) parts.push(scenes === 1 ? '1 scene' : `${scenes} scenes`)
  if (major !== undefined) parts.push(`${major} major`)

  return parts.length === 0 ? undefined : parts.join(' · ')
}
